import { useEffect, useState } from 'react'

import { t } from 'i18next'
import { useFormContext } from 'react-hook-form'

import { useListarImobiliariasCorretores } from '@/features/proposta/hooks'
import type { TPropostaSchemaInput, TPropostaSchemaOutput } from '@/features/proposta/schemas'
import { Button, NumberInputField } from '@/shared/components/ui'
import { AutoCompleteField } from '@/shared/components/ui/auto-complete'
import { showToast } from '@/shared/lib/sonner'

const configuracaoNumberInputPor = {
  percentual: {
    label: t('proposta:detalhes_proposta.imobiliaria.inputs.label_percentual'),
    formatOptions: { minimumFractionDigits: 3, maximumFractionDigits: 3 },
    field: 'percentualCorretagemImobiliaria' as const,
  },
  valor: {
    label: t('proposta:detalhes_proposta.imobiliaria.inputs.label_valor'),
    formatOptions: { minimumFractionDigits: 2, maximumFractionDigits: 2 },
    field: 'valorCorretagemImobiliaria' as const,
  },
}

type TCardImobiliariaCorretorProps = {
  index: number
  onExcluirParticipacao: VoidFunction
}

export const CardImobiliariaCorretor = ({ index, onExcluirParticipacao }: TCardImobiliariaCorretorProps) => {
  const { control, watch, trigger } = useFormContext<TPropostaSchemaInput, never, TPropostaSchemaOutput>()
  const [query, setQuery] = useState('')
  const { data, refetch, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useListarImobiliariasCorretores({ descricao: query, tamanhoDaPagina: 20 })
  const name = `detalhesDaProposta.imobiliariasCorretores.participacoes.${index}` as const

  const imobiliariaCorretor = watch(`${name}`)

  const { field, formatOptions, label } = configuracaoNumberInputPor[imobiliariaCorretor.corretagem.tipo]

  const valorOuPercentualPreenchido = watch(`${name}.corretagem.${field}`)

  useEffect(() => {
    if (valorOuPercentualPreenchido === 0) {
      showToast({
        message: t('proposta:detalhes_proposta.imobiliaria.errors.toast.valor_zerado'),
        variant: 'error',
        closeButton: true,
      })
    }
  }, [valorOuPercentualPreenchido])

  return (
    <div className="flex w-full flex-col gap-4 md:flex-row md:flex-wrap md:items-center md:gap-y-1">
      <AutoCompleteField
        valueKey="idExterno"
        control={control}
        name={`${name}.imobiliariaCorretor`}
        data={data?.pages.flatMap((page) => page.records) ?? []}
        onSearch={setQuery}
        labelKey="descricao"
        label="Imobiliária"
        isLoading={isLoading}
        isError={isError}
        retry={refetch}
        isLoadingMore={isFetchingNextPage}
        onEndReach={hasNextPage ? fetchNextPage : undefined}
        className="w-full truncate md:w-50 xl:w-96"
        onSelect={() => {
          trigger(`detalhesDaProposta.imobiliariasCorretores.participacoes`)
        }}
      />
      <NumberInputField
        className="md:max-w-50 xl:w-96"
        control={control}
        name={`${name}.corretagem.${field}`}
        label={label}
        numberFieldProps={{ formatOptions }}
      />
      <Button type="button" variant="link" className="mb-3 h-10 items-end text-red-800" onClick={onExcluirParticipacao}>
        {t('proposta:detalhes_proposta.imobiliaria.botoes.excluir')}
      </Button>
    </div>
  )
}
